import React from 'react'
import {
  Button,
  Card,
  Div,
  Flex,
  Icon,
  InfoBlock,
  Input,
  SelectableCard,
  Separator,
  Spacer,
  Span,
  Text,
  UserCard,
} from '@sendoutcards/quantum-design-ui'
import {
  useDebounce,
  useEffect,
  useMutations,
  useQueries,
  useQueryParams,
  useSelector,
  useState,
} from 'src/hooks'
import {
  getSponsor,
  getSponsors,
  SponsorFragment,
  VerifyPhoneNumberSuccessFragment,
} from 'src/graphql'
import FindSponsor from 'src/user/components/FindSponsor/FindSponsor'
import { formatLocation } from 'src/helpers'

interface FinishAccountState {
  username: string
  password: string
}

const FinishMyAccount = (props: {
  finishAccount: (
    success: VerifyPhoneNumberSuccessFragment,
    username: string,
    password: string,
    sponsorId: string,
  ) => void
  success: VerifyPhoneNumberSuccessFragment
}) => {
  const { finishAccount, success } = props
  const [values, setValues] = useState<FinishAccountState>({
    username: '',
    password: '',
  })
  const queryParams = useQueryParams()
  const [isUserNameTaken, setIsUserNameTaken] = useState(false)
  const [isPickingSponsor, setIsPickingSponsor] = useState(false)
  const [sponsorId, setSponsorId] = useState('9070')
  const { password, username } = values
  const mutations = useMutations()
  const account = useSelector(state => state.user)

  const affiliate =
    !account.controlData.isLoggedIn && queryParams.sponsor
      ? queryParams.sponsor
      : null

  const [fetchSponsor] = useQueries(
    affiliate
      ? getSponsors({ search: affiliate })
      : sponsorId
      ? getSponsor({ id: sponsorId })
      : undefined,
  )
  const sponsor = Array.isArray(fetchSponsor) ? fetchSponsor[0] : fetchSponsor

  const onSelectedSponsor = (id: string) => {
    setSponsorId(id)
    setIsPickingSponsor(false)
  }

  const handleChange = (name: keyof FinishAccountState, value: string) => {
    setValues({ ...values, [name]: value })
  }
  const debouncedUsername = useDebounce(values.username, 500)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let mutableCanceling = false

    if (debouncedUsername) {
      const validate = async () => {
        const {
          validateUsername: { valid: isValid },
        } = await mutations.validateUsername({ username: debouncedUsername })
        if (mutableCanceling) {
          return
        }
        setIsUserNameTaken(!isValid)
      }
      validate()
    }
    return () => {
      mutableCanceling = true
    }
  }, [debouncedUsername, mutations])

  const handleFinishAccount = async (event: { preventDefault: () => void }) => {
    event.preventDefault()
    finishAccount(success, username, password, sponsorId)
  }

  return (
    <>
      {isPickingSponsor ? (
        <FindSponsor onSelectedSponsor={onSelectedSponsor} />
      ) : (
        <>
          <form onSubmit={handleFinishAccount} autoComplete="on">
            <Icon
              name={'socLogo'}
              size="medium"
              primaryColor={'primaryBrand'}
            />
            <Spacer space="x2" />
            <Text type="title" weight="bold">
              Finish Your Account
            </Text>
            <Spacer space="x2" />
            <Text type="footnote">
              <p style={{ lineHeight: '1.25' }}>
                Looks like you've already sent a card with us!
              </p>
              <p style={{ lineHeight: '1.25' }}>
                Let's finish setting up your account.
              </p>
            </Text>
            <Spacer space="x2" />
            <Div width="100%">
              <Input
                onChange={username => handleChange('username', username)}
                type="text"
                name="username"
                value={username}
                placeholder="Username"
                icon="user"
                message={
                  isUserNameTaken && !!values.username
                    ? { type: 'danger', content: 'This username is taken' }
                    : undefined
                }
                isFullWidth={true}
              />
              <Spacer space="x1" />
              <Input
                onChange={password => handleChange('password', password)}
                type="password"
                name="password"
                value={password}
                placeholder="Password"
                message={{
                  type: 'danger',
                  content: '',
                }}
                icon="lock"
                isFullWidth={true}
              />
              <Spacer space="x1" />
              {sponsor && sponsorId !== '9070' ? (
                <SelectableCard
                  isActive={true}
                  onClick={
                    affiliate ? undefined : () => setIsPickingSponsor(true)
                  }
                >
                  <UserCard
                    profileImage={sponsor.storefront?.mugshot?.url}
                    firstName={sponsor?.firstName}
                    lastName={sponsor?.lastName}
                    caption={formatLocation(
                      (sponsor as SponsorFragment).shippingAddress?.city,
                      (sponsor as SponsorFragment).shippingAddress?.state,
                    )}
                    size="small"
                    active={true}
                  />
                </SelectableCard>
              ) : (
                <Div cursor="pointer">
                  <Card
                    width="100%"
                    borderRadius="medium"
                    inset="x2"
                    onClick={() => setIsPickingSponsor(true)}
                  >
                    <Flex
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Flex alignItems="center">
                        <Span
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          inset={{ right: 'x1' }}
                        >
                          <Icon
                            name="check"
                            primaryColor="default"
                            secondaryColor="background"
                            size="medium"
                          />
                        </Span>
                        <InfoBlock
                          size="small"
                          heading={'Who Showed you this product?'}
                          caption="Optional"
                        />
                      </Flex>
                      <Icon
                        name="information"
                        primaryColor="default"
                        size="small"
                      />
                    </Flex>
                  </Card>
                </Div>
              )}
            </Div>
            <div style={{ marginTop: '16px' }} />
            <Button
              title={'Create My Account'}
              onClick="submitForm"
              fullWidth={true}
              disabled={isUserNameTaken || !values.password || !values.username}
            />
            <Spacer space="x2" />
            <Separator orientation="horizontal" dashed={true} caption="Or" />
            <Spacer space="x2" />
            <Button
              title={'Learn more about our mission'}
              onClick="submitForm"
              fullWidth={true}
              type={'secondary'}
            />
          </form>
        </>
      )}
    </>
  )
}

export default FinishMyAccount
